import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const HERO = "/assets/gledswood/open-day-hero.jpg";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res =
      tab === "login"
        ? await login(email, password)
        : await register(email, password, name);
    setBusy(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grain-bg lg:grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img
          src={HERO}
          alt="Gledswood"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div
          className={[
            "absolute inset-0 bg-gradient-to-t from-[#1F332B]/80",
            "via-[#1F332B]/58 to-[#1F332B]/36",
          ].join(" ")}
        />
        <div
          className={[
            "relative z-10 flex h-full flex-col justify-between p-12",
            "text-primary-foreground",
          ].join(" ")}
        >
          <AuthBrandLockup />
          <div className="max-w-md">
            <p className="font-heading text-3xl font-light italic leading-snug">
              "Event planning, thoughtfully in one place."
            </p>
            <p className="text-white/80 mt-4 text-sm">
              Your personal portal for your celebration at Gledswood Homestead +
              Winery.
            </p>
          </div>
        </div>
      </div>

      <div className="min-h-screen lg:flex lg:items-center lg:justify-center lg:p-12">
        <div className="w-full max-w-md lg:w-full">
          <div
            className="relative h-64 overflow-hidden sm:h-72 lg:hidden"
            data-testid="mobile-auth-hero"
          >
            <img
              src={HERO}
              alt="Gledswood"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div
              className={[
                "absolute inset-0 bg-gradient-to-t from-[#10241A]/90",
                "via-[#1F332B]/58 to-[#10241A]/20",
              ].join(" ")}
            />
            <div className="relative flex h-full flex-col justify-end p-6 text-white sm:p-8">
              <AuthBrandLockup mobile />
            </div>
          </div>

          <div className="px-6 py-8 sm:px-12 lg:px-0 lg:py-0">
            <Tabs
              value={tab}
              onValueChange={(v) => {
                setTab(v);
                setError("");
              }}
            >
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="login" data-testid="tab-login">
                  Sign in
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">
                  Create account
                </TabsTrigger>
              </TabsList>

              <div className="mb-6">
                <h2 className="font-heading text-3xl text-primary font-light">
                  {tab === "login" ? "Welcome" : "Create your account"}
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  {tab === "login"
                    ? "Sign in with the email and password provided by Gledswood, or your own."
                    : "Creating an account doesn't connect you to an event — " +
                      "Gledswood will add you to yours."}
                </p>
              </div>

              <form onSubmit={submit} className="space-y-4">
                {tab === "register" && (
                  <div>
                    <Label htmlFor="name">Your name</Label>
                    <Input
                      id="name"
                      data-testid="register-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="mt-1.5"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1.5"
                  />
                </div>
                {error && (
                  <p
                    data-testid="auth-error"
                    className="text-sm text-destructive"
                  >
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="auth-submit-btn"
                  className={[
                    "w-full rounded-full bg-primary hover:bg-primary/90",
                    "text-primary-foreground",
                  ].join(" ")}
                >
                  {busy
                    ? "Please wait…"
                    : tab === "login"
                      ? "Sign in"
                      : "Create account"}
                  <ArrowRight size={18} className="ml-1" />
                </Button>
              </form>

              <p className="text-center text-xs text-muted-foreground mt-8">
                <Link
                  to="/privacy"
                  data-testid="login-privacy-link"
                  className="underline hover:text-primary"
                >
                  Privacy Policy
                </Link>
              </p>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

function AuthBrandLockup({ mobile = false }) {
  return (
    <div data-testid={mobile ? "mobile-brand-lockup" : "desktop-brand-lockup"}>
      <p
        className={[
          "font-heading font-normal tracking-[0.14em] leading-none",
          mobile ? "text-4xl sm:text-5xl" : "text-6xl",
        ].join(" ")}
      >
        GLEDSWOOD
      </p>
      <div className="mt-3 flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-white/90">
        <span className="h-px flex-1 bg-white/55" />
        <span>HOMESTEAD + WINERY</span>
        <span className="h-px flex-1 bg-white/55" />
      </div>
      <p className="mt-3 text-[10px] font-semibold tracking-[0.24em] uppercase text-white/85">
        Event planning
      </p>
      <h1 className="font-heading mt-2 text-2xl italic text-white/90">Plan</h1>
    </div>
  );
}
